import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { APP_VERSION } from '../version';
import ChangelogModal from './ChangelogModal';
import { isFeatureEnabled } from '../utils/featureFlags';
import { useAdminConfig } from '../contexts/AdminConfigProvider';

interface SettingsDropdownProps {
  userEmail?: string;
}

function SettingsDropdown({ userEmail }: SettingsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isSettingsActive = location.pathname.startsWith('/settings');
  const { isAdmin: checkIsAdmin } = useAdminConfig();

  // Check if user is Admin using the admin list from database
  const isAdmin = checkIsAdmin(userEmail);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on navigation
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-1 text-sm font-medium transition-colors flex items-center gap-1 ${
          isSettingsActive
            ? 'text-white border-b-2 border-blue-500'
            : 'text-slate-400 hover:text-white'
        }`}
      >
        Settings
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-slate-700 border border-slate-600 rounded-lg shadow-lg overflow-hidden z-50">
          {/* Top section - visible to all users */}
          <Link
            to="/settings/user"
            className={`block px-4 py-2 text-sm hover:bg-slate-600 transition-colors ${
              location.pathname === '/settings/user' ? 'text-blue-400 bg-slate-600/50' : 'text-slate-200'
            }`}
          >
            User Settings
          </Link>
          <Link
            to="/settings/feedback"
            className={`block px-4 py-2 text-sm hover:bg-slate-600 transition-colors ${
              location.pathname === '/settings/feedback' ? 'text-blue-400 bg-slate-600/50' : 'text-slate-200'
            }`}
          >
            User Feedback
          </Link>
          <Link
            to="/settings/contact-approvals"
            className={`block px-4 py-2 text-sm hover:bg-slate-600 transition-colors ${
              location.pathname === '/settings/contact-approvals' ? 'text-blue-400 bg-slate-600/50' : 'text-slate-200'
            }`}
          >
            Contact Approvals
          </Link>

          {/* Bottom section - admin only */}
          {isAdmin && (
            <>
              <div className="border-t border-slate-600 my-1" />
              <Link
                to="/settings/bidjobs"
                className={`block px-4 py-2 text-sm hover:bg-slate-600 transition-colors ${
                  location.pathname === '/settings/bidjobs' ? 'text-blue-400 bg-slate-600/50' : 'text-slate-200'
                }`}
              >
                Bid-Job Management
              </Link>
              <Link
                to="/settings/techs"
                className={`block px-4 py-2 text-sm hover:bg-slate-600 transition-colors ${
                  location.pathname === '/settings/techs' ? 'text-blue-400 bg-slate-600/50' : 'text-slate-200'
                }`}
              >
                Tech User Management
              </Link>
              <Link
                to="/settings/admin"
                className={`block px-4 py-2 text-sm hover:bg-slate-600 transition-colors ${
                  location.pathname === '/settings/admin' ? 'text-blue-400 bg-slate-600/50' : 'text-slate-200'
                }`}
              >
                Admin Settings
              </Link>
              <Link
                to="/settings/system"
                className={`block px-4 py-2 text-sm hover:bg-slate-600 transition-colors ${
                  location.pathname === '/settings/system' ? 'text-blue-400 bg-slate-600/50' : 'text-slate-200'
                }`}
              >
                System Settings
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface NavTabsProps {
  searchButton?: React.ReactNode;
  refreshButton?: React.ReactNode;
  userEmail?: string;
}

function NavTabs({ searchButton, refreshButton, userEmail }: NavTabsProps) {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;
  const showCalendar = isFeatureEnabled('calendar');
  const { isAdmin: checkIsAdmin } = useAdminConfig();

  // Check if user is Admin using the admin list from database
  const isAdmin = checkIsAdmin(userEmail);

  const linkClass = (path: string) =>
    `px-3 py-1 text-sm font-medium transition-colors ${
      isActive(path)
        ? 'text-white border-b-2 border-blue-500'
        : 'text-slate-400 hover:text-white'
    }`;

  return (
    <nav className="flex space-x-2 items-center">
      {/* Search button before Dashboard */}
      {searchButton}
      <Link to="/" className={linkClass('/')}>Dashboard</Link>
      <Link to="/tickets" className={linkClass('/tickets')}>Tickets</Link>
      {showCalendar && (
        <Link to="/calendar" className={linkClass('/calendar')}>Calendar</Link>
      )}
      <Link to="/field-map" className={linkClass('/field-map')}>Field Map</Link>
      {isAdmin && (
        <Link to="/reports" className={linkClass('/reports')}>Reports</Link>
      )}
      {/* Refresh button between Reports and Settings */}
      {refreshButton}
      <SettingsDropdown userEmail={userEmail} />
    </nav>
  );
}

interface PageHeaderProps {
  middleContent?: React.ReactNode;
  leftContent?: React.ReactNode;  // Content after branding (for calendar controls)
  searchButton?: React.ReactNode;  // Search button for nav area
  refreshButton?: React.ReactNode;  // Refresh button for nav area
  userEmail?: string;  // For admin settings visibility
  onNewTicket?: () => void;
}

export default function PageHeader({ middleContent, leftContent, searchButton, refreshButton, userEmail, onNewTicket }: PageHeaderProps) {
  const [showChangelog, setShowChangelog] = useState(false);

  return (
    <>
      <div className="flex-shrink-0 z-50 bg-slate-800 border-b border-slate-700 py-1.5 px-3">
        <div className="flex justify-between items-center">
          {/* Left: AutoTask Hub branding + leftContent (calendar controls) */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-xl">🎫</span>
              <div>
                <h1 className="text-lg font-bold text-white leading-tight">AutoTask Hub</h1>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-500">v{APP_VERSION}</span>
                  <button
                    onClick={() => setShowChangelog(true)}
                    className="text-[10px] text-blue-400 hover:underline transition-colors"
                    title="View Changelog"
                  >
                    changelog
                  </button>
                  <a
                    href="https://lively-dune-036b62e0f.4.azurestaticapps.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 hover:text-blue-400 transition-colors"
                    title="Open in new window"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Left content (calendar controls go here) */}
            {leftContent}

            {/* New Ticket Button - only show if no leftContent */}
            {onNewTicket && !leftContent && (
              <button
                onClick={onNewTicket}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                title="Create a new ticket"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Ticket
              </button>
            )}
          </div>

          {/* Center: Page-specific content (only if no leftContent) */}
          {middleContent && !leftContent && (
            <div className="flex items-center">
              {middleContent}
            </div>
          )}

          {/* Right: Nav with optional search/refresh */}
          <div className="text-right">
            <NavTabs searchButton={searchButton} refreshButton={refreshButton} userEmail={userEmail} />
          </div>
        </div>
      </div>

      {/* Changelog Modal */}
      <ChangelogModal isOpen={showChangelog} onClose={() => setShowChangelog(false)} />
    </>
  );
}
